var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { _TypedDataEncoder } from "@ethersproject/hash";
import { BigNumber as EthersBigNumber } from "@ethersproject/bignumber";
import { AddressZero, Zero } from "@ethersproject/constants";
import { ethers } from "ethers";
import BigNumber from "bignumber.js";
import { TransactionManagerNames, } from "../TransactionManager";
import { HttpMethod, sendRequest } from "../../utils/httpRequests";
export const PROXY_FACTORY_NAME = "Polymarket Contract Proxy Factory";
class PolymarketTransactionManager {
    constructor(signer, chainId, urls) {
        this.signer = signer;
        this.chainId = chainId;
        this.urls = urls;
    }
    get config() {
        return {
            name: TransactionManagerNames.PolymarketTransactionManager,
            speed: "rapid", // this does nothing but I thought calling it rapid would be cool
        };
    }
    computeNonce(contract, nonceOffset, nonce) {
        return __awaiter(this, void 0, void 0, function* () {
            if (nonce === undefined) {
                const currentNonce = yield contract.call("nonce", []);
                return EthersBigNumber.from(nonceOffset !== null && nonceOffset !== void 0 ? nonceOffset : 0).add(currentNonce);
            }
            return EthersBigNumber.from(nonce);
        });
    }
    execTransactions(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { isDeployed, safeExecTxParams, contractManager, ethLibAdapter, ownerAccount, nonceOffset, nonce } = options;
            const { contract } = contractManager;
            if (!contract) {
                throw new Error("CPK Proxy contract uninitialized");
            }
            let safeCreationTx;
            if (!isDeployed) {
                safeCreationTx = yield this.deploySafe({
                    contractManager,
                    payment: Zero,
                    paymentReceiver: AddressZero,
                    paymentToken: AddressZero,
                });
                yield safeCreationTx.wait();
            }
            const adjustedNonce = yield this.computeNonce(contract, nonceOffset, nonce);
            // Variables that are constant now but will need to be set dynamically if user's pay for their own gas
            const safeTxGas = "0";
            const baseGas = "0";
            const gasPrice = "0";
            const gasToken = ethers.constants.AddressZero;
            const refundReceiver = ethers.constants.AddressZero;
            const txHash = yield contract.call("getTransactionHash", [
                safeExecTxParams.to,
                safeExecTxParams.value,
                safeExecTxParams.data,
                safeExecTxParams.operation,
                safeTxGas,
                baseGas,
                gasPrice,
                gasToken,
                refundReceiver,
                adjustedNonce,
            ]);
            const rsvSignature = yield this.signTransactionHash(ethLibAdapter, ownerAccount, txHash);
            const packedSig = ethLibAdapter.abiEncodePacked({ type: "uint256", value: rsvSignature.r }, { type: "uint256", value: rsvSignature.s }, { type: "uint8", value: rsvSignature.v });
            const tx = yield this.relayTransaction(contract.address, safeExecTxParams, safeTxGas, baseGas, gasPrice, gasToken, refundReceiver, packedSig);
            return {
                safeTxHash: safeCreationTx && safeCreationTx.hash,
                safeCreationResponse: safeCreationTx,
                hash: tx.hash,
                transactionResponse: tx,
            };
        });
    }
    relayTransaction(safe, tx, safeTxGas, baseGas, gasPrice, gasToken, refundReceiver, signatures) {
        return __awaiter(this, void 0, void 0, function* () {
            const body = {
                safe,
                to: tx.to,
                value: tx.value,
                data: tx.data,
                operation: tx.operation,
                safeTxGas,
                baseGas,
                gasPrice,
                gasToken,
                refundReceiver,
                signatures,
            };
            const randomNum = Math.floor(Math.random() * this.urls.length);
            const url = this.urls[randomNum];
            const jsonResponse = yield sendRequest({
                url: `${url}/relay-safe-tx`,
                method: HttpMethod.POST,
                data: body,
                expectedHttpCodeResponse: 200,
            });
            // request can have an error even if the status code is 200
            if (jsonResponse.error)
                throw new Error(jsonResponse.error);
            return this.signer.provider._wrapTransaction(jsonResponse);
        });
    }
    deploySafe({ paymentToken, payment, paymentReceiver, contractManager, }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!contractManager)
                throw new Error("Contract manager is a required parameter to deploy a safe.");
            const signature = yield this.getCreateProxySignature({
                paymentToken,
                payment,
                paymentReceiver,
                contractManager,
            });
            const body = {
                paymentToken,
                payment: payment.toHexString(),
                paymentReceiver,
                signature,
            };
            const randomNum = Math.floor(Math.random() * this.urls.length);
            const url = this.urls[randomNum];
            const jsonResponse = yield sendRequest({
                url: `${url}/create-safe-proxy`,
                method: HttpMethod.POST,
                data: body,
                expectedHttpCodeResponse: 200,
            });
            // request can have an error even if the status code is 200
            if (jsonResponse.error)
                throw new Error(jsonResponse.error);
            return this.signer.provider._wrapTransaction(jsonResponse);
        });
    }
    getCreateProxySignature({ paymentToken, payment, paymentReceiver, contractManager, }) {
        return __awaiter(this, void 0, void 0, function* () {
            const domain = {
                name: PROXY_FACTORY_NAME,
                chainId: EthersBigNumber.from(this.chainId).toHexString(),
                verifyingContract: contractManager.proxyFactory.address,
            };
            const types = {
                CreateProxy: [
                    { name: "paymentToken", type: "address" },
                    { name: "payment", type: "uint256" },
                    { name: "paymentReceiver", type: "address" },
                ],
            };
            const eip712Value = {
                paymentToken,
                payment,
                paymentReceiver,
            };
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const populated = yield _TypedDataEncoder.resolveNames(domain, types, eip712Value, (name) => {
                return this.signer.provider.resolveName(name);
            });
            const address = yield this.signer.getAddress();
            const message = JSON.stringify(_TypedDataEncoder.getPayload(populated.domain, types, populated.value));
            return this.signer.provider.send("eth_signTypedData_v4", [address.toLowerCase(), message]);
        });
    }
    signTransactionHash(ethLibAdapter, ownerAccount, txHash) {
        return __awaiter(this, void 0, void 0, function* () {
            let sig = yield ethLibAdapter.signMessage(txHash, ownerAccount);
            let sigV = parseInt(sig.slice(-2), 16);
            switch (sigV) {
                case 0:
                case 1:
                    sigV += 31;
                    break;
                case 27:
                case 28:
                    sigV += 4;
                    break;
                default:
                    throw new Error("Invalid signature");
            }
            sig = sig.slice(0, -2) + sigV.toString(16);
            return {
                r: new BigNumber("0x" + sig.slice(2, 66)).toString(10),
                s: new BigNumber("0x" + sig.slice(66, 130)).toString(10),
                v: new BigNumber("0x" + sig.slice(130, 132)).toString(10),
            };
        });
    }
}
export default PolymarketTransactionManager;
